import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import {
	optionsChartReservatorio,
	setXGraficoReservatoriosByArrayOfDates,
} from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/componentes/modal-reservatorios/graficos-reservatorios/chart-options';
import { ReservatorioVolume } from '@home/submodulos/dados-meteorologicos/submodulos/monitoramento/interfaces/reservatorios';
import { TiposDatePicker } from '@shared/interfaces/date-picker';
import ptBrLocale from 'date-fns/locale/pt-BR';
import { format } from 'date-fns';

interface ObjUnidades {
	sigla: string;
	field: string;
	label: string;
}
enum CoresGrafico {
	AZUL = '#0D6A92',
	VERMELHO = '#8E3623',
}
@Component({
	selector: 'seira-graficos-reservatorios',
	templateUrl: './graficos-reservatorios.component.html',
	styleUrls: ['./graficos-reservatorios.component.scss'],
})
export class GraficosReservatoriosComponent implements OnInit, OnChanges {
	@Input() tipoGrafico!: string;
	@Input() unidade!: keyof ReservatorioVolume;
	@Input() dados!: Array<ReservatorioVolume>;
	@Input() title!: string;
	@Input() loading = false;
	@Output() emitterVolume = new EventEmitter<Highcharts.Chart>();
	@Output() emitterAfluencia = new EventEmitter<Highcharts.Chart>();
	@Input() periodo!: TiposDatePicker;
	chartVolume!: Highcharts.Chart;
	chartAfluencia!: Highcharts.Chart;
	optionsVolume!: Highcharts.Options;
	optionsAfluencia!: Highcharts.Options;

	unidadesGraficos: { [p: string]: ObjUnidades } = {
		porcentagem: { sigla: '%', field: 'porcentagem', label: 'Vol. total (%)' },
		volumeAtual: {
			sigla: 'm³',
			field: 'volumeAtual',
			label: 'Vol. atual (m³)',
		},
	};

	ngOnInit() {
		this.updateOptions();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes['loading'] && !this.loading) {
			this.updateOptions();
			this.chartVolume?.hideLoading();
			this.chartAfluencia?.hideLoading();
			this.setChartVolume();
			this.setChartAfluencia();
		}
		if (changes['tipoGrafico'] || changes['unidade'] || changes['dados']) {
			this.updateOptions();
			this.setChartVolume();
			this.setChartAfluencia();
		}
	}

	updateOptions() {
		this.optionsVolume = optionsChartReservatorio([], this.periodo);
		this.optionsAfluencia = optionsChartReservatorio([], this.periodo);
	}

	intanceChartVolume(chart: Highcharts.Chart) {
		if (!this.chartVolume) {
			this.chartVolume = chart;
			this.emitterVolume.emit(chart);
			this.setChartVolume();
		}
	}

	intanceChartAfluencia(chart: Highcharts.Chart) {
		if (!this.chartAfluencia) {
			this.chartAfluencia = chart;
			this.emitterAfluencia.emit(chart);
			this.setChartAfluencia();
		}
	}
	setChartAfluencia() {
		const dataFormatada = format(
			new Date(),
			"dd 'de' MMMM 'de' yyyy 'às' HH'h'mm",
			{
				locale: ptBrLocale,
			}
		);
		this.chartAfluencia?.series?.forEach(serie => serie.remove());

		const data = this.dados
			.filter(el => el.afluencia != null && el.afluencia)
			.sort((a, b) => a.dataColeta - b.dataColeta);

		const afluencias = data.filter(
			el => el.afluencia != undefined && el.afluencia >= 0
		);
		const defluencias = data.filter(
			el => el.afluencia != undefined && el.afluencia < 0
		);

		const optionsAfluencia: any = {
			data: afluencias.map(dado => ({
				x: new Date(dado.dataColeta).getTime(),
				y: dado.afluencia,
			})),
			marker: {
				symbol: 'square',
			},
			name: 'Afluência (m³)',
			color: CoresGrafico.AZUL,
			type: this.tipoGrafico === 'steps' ? 'line' : this.tipoGrafico,
			step: this.tipoGrafico === 'steps',
		};

		const optionsDefluencia: any = {
			data: defluencias.map(dado => ({
				x: new Date(dado.dataColeta).getTime(),
				y: dado.afluencia,
			})),
			marker: {
				symbol: 'square',
			},
			name: 'Defluência (m³)',
			color: CoresGrafico.VERMELHO,
			type: this.tipoGrafico === 'steps' ? 'line' : this.tipoGrafico,
			step: this.tipoGrafico === 'steps',
		};

		const combinedTimestamps = new Set<number>();

		optionsAfluencia.data.forEach((point: any) =>
			combinedTimestamps.add(point.x)
		);
		optionsDefluencia.data.forEach((point: any) =>
			combinedTimestamps.add(point.x)
		);

		const totalPointsAfluencia = combinedTimestamps.size;
		const minWidthAfluencia =
			totalPointsAfluencia > 20 ? totalPointsAfluencia * 25 : 0;

		this.chartAfluencia?.update({
			chart: {
				scrollablePlotArea: {
					minWidth: minWidthAfluencia,
					scrollPositionX: 1,
				},
			},
			yAxis: [
				{
					title: {
						text: 'Afluência (m³)',
						align: 'high',
					},
				},
			],
			xAxis: [
				{
					title: {
						align: 'high',
						text: `Período`,
					},
					type: 'datetime',
				},
			],
			exporting: {
				enabled: true,
				buttons: {
					contextButton: {
						menuItems: [
							'viewFullscreen',
							'separator',
							'downloadPNG',
							'downloadJPEG',
						],
					},
				},
				sourceWidth: 1200,
				sourceHeight: 400,
				scale: 1,
				filename: `Gráfico de afluência do açude exportado em ${dataFormatada}`,
			},
		});
		this.chartAfluencia?.series?.forEach(serie => serie.remove());
		this.chartAfluencia?.addSeries(optionsAfluencia, false);
		this.chartAfluencia?.addSeries(optionsDefluencia, true);
	}

	setChartVolume() {
		const dataFormatada = format(
			new Date(),
			"dd 'de' MMMM 'de' yyyy 'às' HH'h'mm",
			{
				locale: ptBrLocale,
			}
		);
		this.chartVolume?.series?.forEach(serie => serie.remove());
		this.chartVolume?.xAxis?.forEach(axis => axis.remove());

		const data = this.dados
			.filter(el => el.volumeAtual != null)
			.sort((a, b) => a.dataColeta - b.dataColeta);

		this.chartVolume?.addAxis(
			setXGraficoReservatoriosByArrayOfDates(
				data.map(el => el.dataColeta),
				this.periodo
			),
			true
		);

		const options: any = {
			data: data.map(dado => ({
				x: new Date(dado.dataColeta).getTime(),
				y: dado[this.unidade],
			})),
			marker: {
				symbol: 'square',
			},
			name: this.unidadesGraficos[this.unidade].label,
			type: this.tipoGrafico === 'steps' ? 'line' : this.tipoGrafico,
			step: this.tipoGrafico === 'steps',
			color: CoresGrafico.AZUL,
		};

		const totalPointsVolume = options.data.length;
		const minWidthVolume = totalPointsVolume > 20 ? totalPointsVolume * 25 : 0;

		this.chartVolume?.update({
			chart: {
				scrollablePlotArea: {
					minWidth: minWidthVolume,
					scrollPositionX: 1,
				},
			},
			yAxis: [
				{
					title: {
						align: 'high',
						text: `(${this.unidadesGraficos[this.unidade].sigla})`,
					},
				},
			],
			xAxis: [
				{
					title: {
						align: 'high',
						text: 'Período',
					},
					type: 'datetime',
				},
			],
			exporting: {
				enabled: true,
				buttons: {
					contextButton: {
						menuItems: [
							'viewFullscreen',
							'separator',
							'downloadPNG',
							'downloadJPEG',
						],
					},
				},
				sourceWidth: 1200,
				sourceHeight: 400,
				scale: 1,
				filename: `Gráfico de volume do açude exportado em ${dataFormatada}`,
			},
		});

		this.chartVolume?.series.forEach(serie => serie.remove());
		this.chartVolume?.addSeries(options, true);
	}
}
